/**
 * Feature flags.
 *
 * One switch per feature that needs to be hidden without deleting its code, so
 * it can be brought back with a single edit here rather than a revert.
 *
 * Rules of thumb:
 * - Leave the feature's code in place. A flag is for hiding a route, an option
 *   or a panel, not for half-removing the thing behind it.
 * - Read the flag at the edges (the options list, the route table, the render),
 *   not deep inside logic — that keeps the blast radius of flipping it small.
 * - Delete the flag once the decision is permanent in either direction.
 */
export const FEATURE_FLAGS = {
    /**
     * Virtual meetings in the Meeting Hub.
     *
     * Off: "Meeting Type" in Step 1 and "Meeting Format" on the Meeting Summary
     * page both offer Physical only, and every new meeting starts as physical.
     * Everything virtual-related stays in the code — the meeting-link field, the
     * generate-link button, the virtual branches in Step 1 and Step 4 — and
     * comes back by setting this to true.
     *
     * Meetings already saved as virtual keep their stored value; only the
     * choice of creating a new one is hidden.
     */
    virtualMeetings: true,

    /**
     * The Performance vs Target page, in both Royal Momentum and Royal Bridge.
     *
     * Off: the sub-navigation tab is dropped in both portals and the route
     * redirects to Plans & Goals, so a bookmarked URL lands somewhere real
     * instead of on empty page chrome. That leaves a single tab, so both
     * Plans & Targets layouts drop the sub-navigation bar altogether — they
     * render it only while more than one tab survives the flags. On the Royal
     * Momentum homepage it also
     * hides the hero progress card and the "View Performance vs Target" quick
     * action, and skips the revenue-with-target request behind the card.
     *
     * The pages themselves, their routes and every API call stay in the code and
     * come back by setting this to true.
     */
    performanceVsTarget: false,
};

/**
 * @param {keyof typeof FEATURE_FLAGS} name
 * @returns {boolean} true only when the flag is explicitly on, so a typo reads
 * as off rather than as undefined-is-truthy.
 */
export const isFeatureEnabled = (name) => FEATURE_FLAGS[name] === true;
